import type { NodeViewProps } from "@tiptap/react";
import { NodeViewWrapper } from "@tiptap/react";
import { AlertTriangle, CircleAlert } from "lucide-react";
import type { MouseEvent } from "react";
import { Alert, AlertAction, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { DataCatalogEntryNodeOptions } from "../extensions/data-catalog-entry-node";
import { CatalogProviderIcon } from "./catalog-provider-branding";
import {
  catalogStatusBadgeLabel,
  normalizeDataCatalogEntryAttrs,
} from "./data-catalog-entry-shared";

/**
 * "Synced 4h ago" for a catalog embed's freshness chip — the label states what the age is
 * *of*, which is why it is not `formatSmartDate` from `@/lib/formatters`; an embed that has
 * never synced is "Current" rather than a date.
 */
function formatSyncedLabel(lastSyncedAt: number | null) {
  if (!lastSyncedAt) return "Current";
  const deltaMs = Date.now() - lastSyncedAt;
  if (deltaMs < 60_000) return "Synced just now";
  const minutes = Math.floor(deltaMs / 60_000);
  if (minutes < 60) return `Synced ${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Synced ${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `Synced ${days}d ago`;
}

function formatStaleLabel(lastSyncedAt: number | null) {
  if (!lastSyncedAt) return "Catalog data may be outdated. Last synced time is unavailable.";
  const deltaMs = Date.now() - lastSyncedAt;
  const days = Math.floor(deltaMs / 86_400_000);
  if (days < 1) return "Catalog data may be outdated. Last synced today.";
  return `Catalog data may be outdated. Last synced ${days} day${days !== 1 ? "s" : ""} ago.`;
}

export function DataCatalogTemplateEntryNodeView({ node, extension, selected }: NodeViewProps) {
  const attrs = normalizeDataCatalogEntryAttrs(node.attrs as Record<string, unknown>);
  const options = extension.options as DataCatalogEntryNodeOptions | undefined;
  const isBroken = attrs.status === "broken";
  const isStale = attrs.status === "stale";
  const isUnverifiable = attrs.status === "unverifiable";
  const statusBadgeLabel = catalogStatusBadgeLabel(attrs.status);

  const handleClick = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    options?.onOpenDetails?.(attrs);
  };
  const handleRelink = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    options?.onRelink?.(attrs);
  };
  const handleRefresh = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    options?.onRefresh?.(attrs);
  };
  const handleRemove = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    options?.onRemove?.(attrs);
  };

  return (
    <NodeViewWrapper
      as="div"
      contentEditable={false}
      className="df-data-catalog-template-entry-wrapper my-2 max-w-[680px]"
    >
      <div
        className={cn(
          "df-data-catalog-template-entry overflow-hidden rounded-lg border bg-background",
          selected && "ring-2 ring-primary/30",
          isBroken
            ? "border-destructive"
            : isStale
              ? "border-warning"
              : isUnverifiable
                ? "border-neutral-dim"
                : "border-border"
        )}
      >
        {/* Header */}
        <button
          type="button"
          className="flex w-full items-center justify-between gap-2 border-b border-muted px-4 py-2.5 text-left font-sans not-italic"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleClick}
        >
          <div className="flex min-w-0 items-center gap-2">
            <CatalogProviderIcon
              providerKey={attrs.providerKey}
              className={cn("size-3.5 shrink-0", isBroken ? "text-neutral-dim" : "text-primary")}
            />
            <span
              className={cn(
                "truncate text-[13px] font-semibold",
                isBroken ? "text-neutral-dim" : "text-foreground"
              )}
            >
              {attrs.objectDisplayLabel}
            </span>
            <span
              className={cn(
                "shrink-0 rounded-sm px-1.5 py-0.5 text-[10px] font-medium",
                isBroken ? "bg-muted text-neutral-dim" : "bg-surface-blue text-blue-content"
              )}
            >
              {attrs.templateLabel}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {isBroken ? (
              <span className="rounded-sm bg-surface-red px-1.5 py-0.5 text-[10px] font-medium text-red-content">
                {statusBadgeLabel}
              </span>
            ) : isStale ? (
              <span className="rounded-sm bg-surface-amber px-1.5 py-0.5 text-[10px] font-medium text-amber-content">
                {statusBadgeLabel}
              </span>
            ) : isUnverifiable ? (
              <span className="rounded-sm bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                {statusBadgeLabel}
              </span>
            ) : (
              <span className="text-[11px] text-success">
                {formatSyncedLabel(attrs.lastSyncedAt)}
              </span>
            )}
          </div>
        </button>

        {/* Stale banner */}
        {isStale ? (
          <Alert variant="warning" className="rounded-none border-x-0 border-t-0 px-4 py-2">
            <AlertTriangle />
            <AlertTitle>{formatStaleLabel(attrs.lastSyncedAt)}</AlertTitle>
            <AlertAction>
              <Button type="button" variant="outline" size="sm" onClick={handleRefresh}>
                Refresh
              </Button>
            </AlertAction>
          </Alert>
        ) : null}

        {/* Unverifiable banner — the embed keeps the content it last verified */}
        {isUnverifiable ? (
          <Alert className="rounded-none border-x-0 border-t-0 px-4 py-2">
            <AlertTriangle />
            <AlertTitle>
              Catalog data could not be verified as of the last refresh attempt. Content shown may
              be outdated.
            </AlertTitle>
            <AlertAction>
              <Button type="button" variant="outline" size="sm" onClick={handleRefresh}>
                Retry
              </Button>
            </AlertAction>
          </Alert>
        ) : null}

        {/* Broken content */}
        {isBroken ? (
          <Alert variant="destructive" className="rounded-none border-0 px-4 py-2.5">
            <CircleAlert />
            <AlertTitle>Asset not found in catalog</AlertTitle>
            <AlertDescription>
              {attrs.brokenReason ||
                "This asset may have been deleted or moved. Re-link to a different asset or remove this embed."}
              {options?.onRelink || options?.onRemove ? (
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {options?.onRelink ? (
                    <Button type="button" variant="outline" size="sm" onClick={handleRelink}>
                      Re-link
                    </Button>
                  ) : null}
                  {options?.onRemove ? (
                    <Button type="button" variant="ghost" size="sm" onClick={handleRemove}>
                      Remove
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : (
          /* Field rows */
          <div className="flex flex-col gap-2 px-4 py-2.5 font-sans not-italic">
            {attrs.selectedFieldLabels.map((fieldLabel, index) => (
              <div
                key={
                  attrs.selectedFieldKeys[index] ??
                  `${fieldLabel}-${attrs.selectedFieldValues[index] ?? index}`
                }
                className="flex items-baseline justify-between gap-4 text-[12px]"
              >
                <span className="shrink-0 text-neutral-dim">{fieldLabel}</span>
                <span className="min-w-0 text-right text-foreground">
                  {attrs.selectedFieldValues[index] ?? "\u2014"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </NodeViewWrapper>
  );
}
